import * as ko from 'knockout';
import {TBaseOrObservableOrFunctionType} from '../../index';
import {getAnyValue} from '../../util/misc';
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

export type TCalloutVariant = 'info' | 'warning' | 'success' | 'tip' | 'danger';

const DEFAULT_ICONS: Record<TCalloutVariant, string> = {
    info: 'fa-info-circle',
    warning: 'fa-exclamation-triangle',
    success: 'fa-check-circle',
    tip: 'fa-lightbulb',
    danger: 'fa-exclamation-triangle',
};

export interface ICalloutParams {
    /** Visual tone; defaults to `info`. */
    variant?: TBaseOrObservableOrFunctionType<TCalloutVariant>;
    /** Optional translated title key (`t:` binding). Omit for body-only (icon + text). */
    title?: TBaseOrObservableOrFunctionType<string>;
    /** Plain-text body (not a translation key). */
    body?: TBaseOrObservableOrFunctionType<string>;
    /** Override the variant default Font Awesome icon. */
    icon?: TBaseOrObservableOrFunctionType<string>;
    dismissible?: TBaseOrObservableOrFunctionType<boolean>;
    onDismiss?: () => void;
}

/** Design-system callout: left accent bar, icon, optional title, body, optional dismiss. */
export class viewModel {
    readonly rootCss: KnockoutComputed<string>;
    readonly title: KnockoutComputed<string>;
    readonly body: KnockoutComputed<string>;
    readonly icon: KnockoutComputed<string>;
    readonly dismissible: KnockoutComputed<boolean>;

    constructor(private readonly params: ICalloutParams) {
        this.rootCss = ko.pureComputed(() => {
            const variant = this.resolveVariant();
            return `ds-callout--${variant}`;
        });
        this.title = ko.pureComputed(() => String(getAnyValue(this.params.title) ?? '').trim());
        this.body = ko.pureComputed(() => String(getAnyValue(this.params.body) ?? '').trim());
        this.icon = ko.pureComputed(() => {
            const override = String(getAnyValue(this.params.icon) ?? '').trim();
            if (override) {
                return override;
            }
            return DEFAULT_ICONS[this.resolveVariant()];
        });
        this.dismissible = ko.pureComputed(() => !!getAnyValue(this.params.dismissible));
    }

    onDismissClick = (): void => {
        this.params.onDismiss?.();
    };

    dispose(): void {
        this.rootCss.dispose();
        this.title.dispose();
        this.body.dispose();
        this.icon.dispose();
        this.dismissible.dispose();
    }

    private resolveVariant(): TCalloutVariant {
        const raw = String(getAnyValue(this.params.variant) ?? 'info');
        if (raw in DEFAULT_ICONS) {
            return raw as TCalloutVariant;
        }
        return 'info';
    }
}
